import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, Sparkles } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useLanguage } from "../../contexts/LanguageContext";
import Reveal from "../Reveal";

export default function FAQAccordion({ content }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(0);

  const faqs = Array.from({ length: 6 })
    .map((_, i) => ({
      q: content?.[`faq_${i + 1}_q`],
      a: content?.[`faq_${i + 1}_a`],
    }))
    .filter((f) => f.q && f.a);

  if (!faqs.length) return null;

  // Schema.org FAQPage markup
  const ldJson = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  return (
    <section data-testid="faq-section" className="bg-cream py-section sm:py-section-lg border-t border-mist">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(ldJson)}</script>
      </Helmet>
      <div className="container-page">
        <Reveal as="div" className="max-w-lg mb-12">
          <span className="eyebrow"><Sparkles className="h-3 w-3 text-gold" />{content?.faq_eyebrow || t("service.faq_eyebrow")}</span>
          <h2 className="mt-5 font-display text-display-2 font-semibold text-ink leading-tight">
            {content?.faq_title || t("service.faq_title")}
          </h2>
          <div className="hairline-gold mt-8" />
        </Reveal>

        <div className="max-w-3xl">
          <ul className="divide-y divide-mist border-t border-b border-mist">
            {faqs.map((f, i) => {
              const isOpen = open === i;
              return (
                <li key={i} data-testid={`faq-item-${i + 1}`}>
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    aria-expanded={isOpen}
                    className="w-full flex items-start justify-between gap-6 text-start py-6 group"
                    data-testid={`faq-question-${i + 1}`}
                  >
                    <span className="font-display text-h3 font-medium text-ink leading-snug">
                      {f.q}
                    </span>
                    <span
                      className={"flex-shrink-0 mt-1 inline-flex h-9 w-9 items-center justify-center rounded-full border transition-colors duration-300 " +
                        (isOpen ? "bg-ink border-ink text-cream" : "border-mist text-ink/70 group-hover:border-ink group-hover:text-ink")}
                    >
                      {isOpen ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="pb-6 pe-12 text-body text-ink/70 leading-relaxed" data-testid={`faq-answer-${i + 1}`}>
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}
